import type { GameRunResult, SceneAttempt } from "@/types/gameSpec";
import type { ArchetypeId } from "@/types/quizGameSpec";

/** Shape of a single per-question attempt used by every archetype. */
export interface ArchetypeAttempt {
  prompt: string;
  correct: string;
  answered: string;
  isCorrect: boolean;
  timeMs: number;
}

/**
 * Convert a list of per-question attempts into the app's shared
 * GameRunResult shape so analytics + ResultScreen keep working.
 */
export function attemptsToRunResult(input: {
  archetype: ArchetypeId;
  attempts: ArchetypeAttempt[];
  startedAt: number;
  won: boolean;
  score: number;
  maxScore: number;
  streakMax: number;
}): GameRunResult {
  const scenes: SceneAttempt[] = input.attempts.map((a, i) => ({
    scene_id: `${input.archetype}-${i}`,
    scene_index: i,
    // SceneType is a legacy union; use the archetype id for analytics fidelity
    scene_type: input.archetype as unknown as SceneAttempt["scene_type"],
    is_correct: a.isCorrect,
    points_awarded: a.isCorrect ? 1 : 0,
    time_taken_ms: a.timeMs,
    details: { prompt: a.prompt, correct: a.correct, answered: a.answered },
  }));

  return {
    spec_version: 1,
    score: input.score,
    max_score: input.maxScore,
    won: input.won,
    scene_count: input.attempts.length,
    scenes_correct: input.attempts.filter((a) => a.isCorrect).length,
    streak_max: input.streakMax,
    time_spent_ms: Date.now() - input.startedAt,
    attempts: scenes,
  };
}

/** Deterministic shuffle so retries feel varied but tests can seed if needed. */
export function shuffle<T>(list: T[]): T[] {
  const arr = list.slice();
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}
